import moment from 'moment';
import Mustache from 'mustache';
import Remarkable from 'remarkable';
import { slugify, i18n } from '../../helpers';

const md = new Remarkable({ html: true, breaks: false });

export function generateContextForMessage(opts = {}) {
  const { account, currentUser, signatureSetting, website, relatedObject, recipientEmail, searchAvailabilities, lastPayment, lastSentMessage } = opts;
  const language = opts.language || 'en';
  // Sender
  let sender = {};
  if (currentUser) {
    sender = {
      display_name: currentUser.name,
      last_name: currentUser.name,
      email: currentUser.email,
    };
  } else if (account) {
    sender = {
      display_name: account.name,
      last_name: account.name,
      email: account.email,
    };
  }

  const recipient = {};
  const reservation = { asset: {} };
  // Recipient
  if (recipientEmail) {
    if (recipientEmail.match('<')) {
      const [displayName, email] = recipientEmail.split(' <');
      recipient.display_name = displayName;
      if (displayName.match(' ')) {
        const [fistName, lastName] = /^([^\s]+)\s(.*)$/.exec(displayName).slice(1);
        recipient.first_name = fistName;
        recipient.last_name = lastName;
      } else {
        recipient.first_name = displayName;
      }
      recipient.email = email.split('>')[0];
    } else {
      recipient.email = recipientEmail;
    }
  }
  if (relatedObject) {
    if (!recipientEmail) {
      // Customer
      const customer = relatedObject.customer;
      if (customer) {
        const entity = customer.entity || {};
        recipient.display_name = customer.name || `${entity.first_name || ''} ${entity.last_name || ''}`;
        recipient.prefix = entity.title;
        recipient.first_name = entity.first_name;
        recipient.last_name = entity.last_name;
        recipient.email = entity.primary_email;
        recipient.language = customer.language;
      }
    }
    // Reservation
    reservation.date = moment(relatedObject.creation_date).format('DD/MM/YYYY');
    reservation.origin = relatedObject.origin;
    reservation.original_reference = relatedObject.original_reference || relatedObject.reference;
    reservation.message = relatedObject.message;
    reservation.remarks = relatedObject.remarks;
    reservation.transport = relatedObject.arrival_transport;
    reservation.checkin = moment(relatedObject.arrival_date).format('DD/MM/YYYY');
    reservation.checkin_time = relatedObject.arrival_time || '';
    reservation.checkout = moment(relatedObject.departure_date).format('DD/MM/YYYY');
    reservation.nb_nights = relatedObject.nb_nights || 0;
    reservation.nb_total_people = relatedObject.nb_total_people || 0;
    reservation.nb_adults = relatedObject.nb_total_adults || 0;
    reservation.nb_children = relatedObject.nb_total_children || 0;
    reservation.nb_babies = relatedObject.nb_babies || 0;
    reservation.has_babies = relatedObject.nb_babies && relatedObject.nb_babies > 0;
    if (relatedObject.price_not_communicated) {
      reservation.deposit = 0;
      reservation.base_price = 0;
      reservation.vat = 0;
      reservation.vat_amount = 0;
      reservation.cleaning_cost = 0;
      reservation.tourist_tax = 0;
      reservation.total_price = 0;
      reservation.total_price_without_tourist_tax = 0;
      reservation.total_price_with_tax = 0;
      reservation.base_price_per_night = 0;
      reservation.price_per_night_and_person = 0;
    } else {
      reservation.deposit = relatedObject.deposit;
      reservation.base_price = relatedObject.base_price.toFixed(2);
      reservation.vat = relatedObject.vat;
      reservation.vat_amount = relatedObject.vat_amount;
      reservation.cleaning_cost = relatedObject.cleaning_fee;
      reservation.tourist_tax = relatedObject.tourist_tax;
      reservation.total_price = relatedObject.total_price.toFixed(2);
      reservation.total_price_without_tourist_tax = relatedObject.total_price_without_tourist_tax.toFixed(2);
      reservation.total_price_with_tax = relatedObject.total_price.toFixed(2);
      reservation.base_price_per_night = relatedObject.base_price_per_night.toFixed(2);
      reservation.price_per_night_and_person = relatedObject.total_price_per_night_per_person.toFixed(2);
    }
    reservation.has_vat = reservation.vat_amount && reservation.vat_amount > 0;
    reservation.has_cleaning_cost = reservation.cleaning_cost && reservation.cleaning_cost > 0;
    reservation.has_tourist_tax = relatedObject.tourist_tax && relatedObject.tourist_tax > 0;
    reservation.payment_url = `${website.url}/${language}/${website.module_slugs[language]}/booking-form?booking_code=${relatedObject.reference}`;
    // Search url on website
    const searchUrl = `${website.url}/${language}/${website.module_slugs[language]}/search-apartments`;
    let queryParams = `?arrival_date=${reservation.checkin}&departure_date=${reservation.checkout}`;
    queryParams += `&nb_adults_26p=${relatedObject.nb_adults}&nb_youngs_16_25=${relatedObject.nb_young_adults}&nb_children_8_15=${relatedObject.nb_children}&nb_children_4_7=${relatedObject.nb_young_children}`;
    queryParams += `&nb_babies=${relatedObject.nb_babies}`;
    reservation.offer_url = searchUrl + queryParams;

    // Asset
    const property = relatedObject.property;
    if (property) {
      const content = property.content || {};
      const emailData = property.email_data || {};
      const slug = i18n(property.slug, language);
      let propertyUrl = `${website.url}/${language}/${website.module_slugs[language]}/${slug}`;
      if (relatedObject) {
        propertyUrl += `?requesting=1&arrival_date=${moment(relatedObject.arrival_date).format('DD/MM/YYYY')}&departure_date=${moment(relatedObject.departure_date).format('DD/MM/YYYY')}`;
        propertyUrl += `&nb_adults_26p=${relatedObject.nb_adults}&nb_youngs_16_25=${relatedObject.nb_young_adults}&nb_children_8_15=${relatedObject.nb_children}&nb_children_4_7=${relatedObject.nb_young_children}`;
        propertyUrl += `&nb_babies=${relatedObject.nb_babies}`;
      }
      let textMailPresentation = (emailData.presentation || {}).$i18n;
      if (textMailPresentation) {
        textMailPresentation = (textMailPresentation[language] || '')
          .replace(/\\_LINK\\_TO\\_APARTMENT/, `<a href="${propertyUrl}">${property.name}</a>`);
      }
      const rentalRuleset = property.daily_rental_ruleset || {};
      const deposit = rentalRuleset.deposit;
      const unit = relatedObject.unit_id && property.units
        ? property.units.find(u => parseInt(u.number) === parseInt(relatedObject.unit_id)) || {}
        : {};
      reservation.asset = {
        name: property.name,
        type: property.type,
        fullname: unitFullname(unit, property),
        url: propertyUrl,
        nb_bedrooms: property.nb_bedrooms,
        nb_bathrooms: property.nb_bathrooms,
        deposit: deposit ? deposit.value : 0,
        zone: property.zone ? i18n(property.zone.name, language) : '',
        area: property.main_area ? i18n(property.main_area.name, language) : '',
        address: `${property.street_address || ''}, ${property.postcode || ''} ${property.locality || ''}`,
        text_mail_presentation: textMailPresentation,
      };
      reservation.item_description = unit.description;
    }
  }
  // Alternative offer
  let availabilities = [];
  if (searchAvailabilities) {
    availabilities = searchAvailabilities.map(a => {
      const slug = i18n(a.slug, language);
      let url = `${website.url}/${language}/${website.module_slugs[language]}/${slug}`;
      if (relatedObject) {
        url += `?requesting=1&arrival_date=${moment(relatedObject.arrival_date).format('DD/MM/YYYY')}&departure_date=${moment(relatedObject.departure_date).format('DD/MM/YYYY')}`;
        url += `&nb_adults_26p=${relatedObject.nb_adults}&nb_youngs_16_25=${relatedObject.nb_young_adults}&nb_children_8_15=${relatedObject.nb_children}&nb_children_4_7=${relatedObject.nb_young_children}`;
        url += `&nb_babies=${relatedObject.nb_babies}`;
      }
      return {
        name: a.name,
        nb_bedrooms: a.nb_bedrooms,
        nb_bathrooms: a.nb_bathrooms,
        url,
        base_price_per_night: a.base_price_per_night.toFixed(2),
        vat: a.vat,
        has_cleaning_cost: a.cleaning_fee > 0,
        cleaning_cost: a.cleaning_fee,
        total_price: a.total_price.toFixed(2),
        total_price_without_tourist_tax: a.total_price_without_tourist_tax.toFixed(2),
      };
    });
  }
  // Last payment
  const payment = {};
  if (lastPayment) {
    payment.amount = lastPayment.amount;
    payment.currency = lastPayment.currency;
  }
  // Last sent message
  const date = {};
  if (lastSentMessage) {
    date.last_message_sent = moment(lastSentMessage.date).format('DD/MM/YYYY');
  }

  // Signature
  let signature = '';
  if (signatureSetting) {
    const content = signatureSetting.mode === 'markdown'
      ? md.render(signatureSetting.value.content || '')
      : signatureSetting.value.content;
    signature = Mustache.render(content || '', { sender, recipient });
  }

  return { sender, recipient, signature, booking: reservation, availabilities, payment, date };
}

function unitFullname(unit, property) {
  if (unit && (property.units || []).length >= 2) {
    return `${property.name}-${unit.number}`;
  }
  return property.name;
}



// WEBPACK FOOTER //
// ./src/backend/email/api/context.js